import {
    afterNextRender,
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    output,
    signal,
    viewChild,
    WritableSignal
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { rxResource } from '@angular/core/rxjs-interop';
import { debounceTime, Subject } from 'rxjs';

import { ApContentService } from '@ap/content';
import { ApChatTextareaComponent } from '@ap/textarea/chat';

import { ChatUIStore } from '../../../signal-stores/chat-ui-store/chat-ui.store';
import { ConversationalChatStore } from '../../../signal-stores/conversational-chat-store/conversational-chat.store';
import { Platform } from '../../conversational-connector/klant-conversational-connector.model';

import content from './conversational-chat-input.content.json';

@Component({
    selector: 'conversational-chat-input',
    templateUrl: './conversational-chat-input.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConversationalChatInputComponent {
    private readonly apContentService = inject(ApContentService);
    private readonly chatUIStore = inject(ChatUIStore);
    private readonly conversationalChatStore = inject(ConversationalChatStore);

    public readonly hideSafetyNotification: WritableSignal<boolean> = signal(false);
    public readonly sendMessage = output<string>();

    public readonly inputFieldControl: FormControl<string> = new FormControl();

    private readonly apChatTextArea = viewChild.required(ApChatTextareaComponent, { read: ElementRef });
    private readonly scrollDispatchSubject = new Subject<boolean>();
    private readonly scrollDispatchResource = rxResource({
        stream: () => this.scrollDispatchSubject.asObservable().pipe(debounceTime(200))
    });
    private readonly shouldScroll = computed(() => this.scrollDispatchResource.value());

    public constructor() {
        this.apContentService.addContentItems(content);

        afterNextRender({
            read: () => {
                this.addFocusListener();
            }
        });

        afterRenderEffect({
            read: () => {
                if (this.shouldScroll() === true) {
                    this.chatUIStore.dispatchScrollAction({ behaviour: 'auto' });
                    this.scrollDispatchSubject.next(false);
                }
            }
        });
    }

    public onSendChatMessage(): void {
        const value = this.inputFieldControl.value?.trim();

        if (value) {
            this.sendMessage.emit(value);
            this.inputFieldControl.setValue('');
            this.hideSafetyNotification.set(true);
        }
    }

    private addFocusListener(): void {
        if (this.conversationalChatStore.platform() !== Platform.MOBIEL) {
            return;
        }

        this.apChatTextArea()
            .nativeElement.querySelector('textarea')
            .addEventListener('focus', () => {
                this.scrollDispatchSubject.next(true);
            });
    }
}
